//getReportedCatchYears

import React, {useEffect, useState} from 'react';
import {Button, Form, Modal} from 'react-bootstrap';
import EmptyPage from '../../components/EmptyPage';
import {CatchYear} from '../../dtos/CatchYear';
import {NetworkCalls} from '../../NetworkCalls';
import {ReportedCatchYearDoneDTO} from '../../dtos/ReportedCatchYearDoneDTO';
import FangkarteList from '../../components/FangkarteList';
import TimestampFormat from '../../utils/TimestampFormat';
import {FangkarteListItem} from '../../models/FangkarteListItem';
import axios from 'axios';
import DeleteDialog from '../../components/DeleteDialog';
import {useSelectedOrganisationContext} from "../../custom-hooks/useOrganisation";

export default function ReportedCatchYears({
                                             reportedCatchYear,
                                             onCountChange
                                           }: {
  reportedCatchYear: CatchYear | undefined;
  onCountChange?: (count: number) => void;
}) {
  const [reportedCatchYears, setReportedCatchYears] = useState<
    ReportedCatchYearDoneDTO[]
  >([]);
  const [showAddDialog, setShowAddDialog] = useState<boolean>(false);
  const [memberNumber, setMemberNumber] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [statusMessage, setStatusMessage] = useState<string | undefined>(undefined);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedReport, setSelectedReport] =
    useState<ReportedCatchYearDoneDTO | undefined>(undefined);

  const { selectedOrganisation } = useSelectedOrganisationContext();

  useEffect(() => {
    if (reportedCatchYear !== undefined) {
      const fetchData = async () => {
        const result = await NetworkCalls.getReportedCatchYears(reportedCatchYear);
        setReportedCatchYears(result);
        if (onCountChange) {
          onCountChange(result.length);
        }
      };

      fetchData().catch(console.error);
    } else {
      setReportedCatchYears([]);
      if (onCountChange) {
        onCountChange(0);
      }
    }
  }, [reportedCatchYear, onCountChange]);

  useEffect(() => {
    const value = memberNumber.trim();
    if (!value) {
      setStatusMessage(undefined);
      return;
    }
    const alreadyReported = reportedCatchYears.find(
      (report) => report.number === value
    );
    if (alreadyReported) {
      setStatusMessage('Bereits abgeschlossen.');
    } else {
      setStatusMessage('Noch offen.');
    }
  }, [memberNumber, reportedCatchYears]);

  const createListItems = () => {
    return reportedCatchYears.map((report) => {
      const date = TimestampFormat.toDateOnlyReadableFormat(report.yearClosed);
      const title = `${report.firstName} ${report.lastName} (${report.number})`;
      return {
        title: title,
        subTitle: date,
        originalElement: report
      } as FangkarteListItem;
    });
  };

  const reportSelected = (report: ReportedCatchYearDoneDTO) => {
  };

  const getContent = () => {
    return (
      <div className="col-12 h-100 d-flex flex-column" style={{minHeight: 0}}>
        <FangkarteList
          listItems={createListItems()}
          onItemClicked={reportSelected}
          title="Einträge"
          customDownloadLink={
            reportedCatchYear?.id
              ? `${axios.defaults.baseURL}/report-catch-year/list/${selectedOrganisation?.id}/${reportedCatchYear.id}/isl-export-csv`
              : undefined
          }
          downloadLabel="Download"
          proSearchEnabled={true}
          scrollable={true}
          addNewElement={true}
          addButtonLabel="Neu"
          addButtonClassName="btn-sm"
          actionsInMenu={true}
          deleteElement={true}
          onItemDelete={(elem) => {
            setSelectedReport(elem);
            setShowDeleteDialog(true);
          }}
          onItemAdd={() => {
            setMemberNumber('');
            setError(undefined);
            setShowAddDialog(true);
          }}
        />
        <DeleteDialog
          onDeleteConfirm={async () => {
            setShowDeleteDialog(false);
            const catchYearId = reportedCatchYear?.id;
            if (!selectedReport || !catchYearId || !selectedOrganisation) {
              return;
            }
            try {
              await NetworkCalls.deleteReportedCatchYearAdmin({
                organisationId: selectedOrganisation.id,
                catchYearId,
                number: selectedReport.number
              });
              const result = await NetworkCalls.getReportedCatchYears(
                reportedCatchYear
              );
              setReportedCatchYears(result);
              if (onCountChange) {
                onCountChange(result.length);
              }
            } catch (e) {
              setError('Löschen fehlgeschlagen.');
            } finally {
              setSelectedReport(undefined);
            }
          }}
          title="Löschen"
          message={`Eintrag wirklich löschen${
            selectedReport?.number ? ` (Mitglied ${selectedReport.number})` : ''
          }?`}
          show={showDeleteDialog}
          onHandleClose={() => {
            setShowDeleteDialog(false);
            setSelectedReport(undefined);
          }}
        />
        <Modal
          show={showAddDialog}
          onHide={() => setShowAddDialog(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Fangjahr melden</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label className="required">Mitgliedsnummer</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Mitgliedsnummer"
                  value={memberNumber}
                  onChange={(e) => setMemberNumber(e.target.value)}
                  disabled={saving}
                />
              </Form.Group>
              {statusMessage ? (
                <div
                  className={
                    statusMessage === 'Bereits abgeschlossen.'
                      ? 'text-danger mt-2'
                      : 'text-success mt-2'
                  }
                >
                  {statusMessage}
                </div>
              ) : null}
              {error ? (
                <div className="text-danger mt-2">{error}</div>
              ) : null}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowAddDialog(false)}
              disabled={saving}
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              disabled={
                !memberNumber ||
                saving ||
                !reportedCatchYear ||
                statusMessage === 'Bereits abgeschlossen.'
              }
              onClick={async () => {
                const catchYearId = reportedCatchYear?.id;
                if (!catchYearId || !selectedOrganisation) {
                  return;
                }
                setSaving(true);
                setError(undefined);
                try {
                  await NetworkCalls.reportCatchYearAdmin({
                    organisationId: selectedOrganisation.id,
                    number: memberNumber,
                    catchYearId
                  });
                  const result = await NetworkCalls.getReportedCatchYears(
                    reportedCatchYear
                  );
                  setReportedCatchYears(result);
                  if (onCountChange) {
                    onCountChange(result.length);
                  }
                  setShowAddDialog(false);
                } catch (e) {
                  setError('Meldung fehlgeschlagen.');
                } finally {
                  setSaving(false);
                }
              }}
            >
              Speichern
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  };

  return (
    <>
      {reportedCatchYear === undefined || selectedOrganisation === null ? <EmptyPage /> : getContent()}
    </>
  );
}
